import React, { useState, useEffect } from "react";
import {
  IconButton,
  MenuItem,
  MenuList,
  Typography,
  Box,
  Drawer,
  Divider,
  ListItemButton,
  List,
  ListItemIcon,
  ListItem,
  ListItemText,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import { getProfile } from "../redux/slices/profileSlice";
import { FitnessCenter as FitnessCenterIcon } from "@mui/icons-material";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { clearAuthToken } from "../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import {
  clearCurrentWorkout,
  clearCurrentTemplate,
  createWorkout,
} from "../redux/slices/workoutSlice";
import FeedbackDialog from "./Account/Feedback/FeedbackDialog";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import DescriptionIcon from "@mui/icons-material/Description";

const NavBar = ({ isMenuDisabled, handleInstallClick, isInstallable }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { token } = useSelector((state) => state.authSlice);
  const { workoutTemplates, admin } = useSelector(
    (state) => state.profileSlice
  );
  const { workout_id } = useSelector((state) => state.workoutSlice);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [navigateToNewWorkout, setNavigateToNewWorkout] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    if (workout_id && navigateToNewWorkout) {
      navigate(`/workout/${workout_id}`);
    }
  }, [workout_id, navigateToNewWorkout, navigate]);

  const handleMenuOpen = (event) => {
    setDrawerOpen(true);
  };

  const handleMenuClose = () => {
    setDrawerOpen(false);
  };

  const handleNavigateToTemplateConfig = (template_id) => {
    navigate(`/workout-template/${template_id}`);
  };

  const handleViewTemplates = () => {
    setDrawerOpen(false);
    dispatch(getProfile({ token }));
    dispatch(clearCurrentWorkout());
    navigate("/workout-template-select");
  };

  const handleLogout = () => {
    setDrawerOpen(false);
    dispatch(clearAuthToken());
    dispatch(clearCurrentWorkout());
    navigate("/welcome");
  };

  const handleCreateBlankWorkout = async () => {
    const is_template = false;
    dispatch(clearCurrentWorkout());
    dispatch(clearCurrentTemplate());
    await dispatch(createWorkout({ token, is_template }));
    setNavigateToNewWorkout(true);
  };

  const handleNavigateToWorkouts = () => {
    setDrawerOpen(false);
    dispatch(getProfile({ token }));
    dispatch(clearCurrentWorkout());
    navigate("/workouts");
  };

  const handleNavigateHome = () => {
    setDrawerOpen(false);
    if (token) {
      dispatch(getProfile({ token }));
      dispatch(clearCurrentWorkout());
      dispatch(clearCurrentTemplate());
      navigate("/");
    } else {
      navigate("/welcome");
    }
  };

  const handleReportFeedback = async (feedback, feedbackType) => {
    await axios.post(
      `/api/account/createFeedback`,
      {
        feedback,
        feedbackType,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const handleToggleFeedbackDialog = () => {
    setFeedbackOpen(!feedbackOpen);
  };

  return (
    <Box>
      {isMenuDisabled ? (
        <Box>
          <IconButton
            size="large"
            edge="end"
            color={theme.palette.text.primary}
            onClick={handleMenuOpen}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            open={drawerOpen}
            onClick={drawerOpen ? handleMenuClose : handleMenuOpen}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                paddingTop: "16px",
                paddingLeft: "16px",
                paddingBottom: "16px",
                paddingRight: "16px",
              }}
            >
              <FitnessCenterIcon sx={{ marginRight: "8px" }} />
              <Typography
                variant="h6"
                sx={{ flexGrow: 1, color: theme.palette.text.primary }}
              >
                Weightliftr
              </Typography>
            </Box>
            <MenuList>
              <MenuItem onClick={() => navigate("/login")}>Login</MenuItem>
              <MenuItem onClick={() => navigate("/register")}>Register</MenuItem>
              <MenuItem onClick={() => navigate("/blog")}>Blog</MenuItem>
              <MenuItem onClick={() => navigate("/welcome")}>Home</MenuItem>
              {isInstallable ? (
                <MenuItem onClick={handleInstallClick}>Install App</MenuItem>
              ) : null}
            </MenuList>
          </Drawer>
        </Box>
      ) : (
        <Box>
          <Box>
            <IconButton
              size="large"
              edge="end"
              color={theme.palette.text.primary}
              onClick={handleMenuOpen}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Drawer
            open={drawerOpen}
            onClick={drawerOpen ? handleMenuClose : handleMenuOpen}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                paddingTop: "16px",
                paddingLeft: "16px",
                paddingBottom: "16px",
                paddingRight: "16px",
              }}
            >
              <FitnessCenterIcon sx={{ marginRight: "8px" }} />
              <Typography
                variant="h6"
                sx={{ flexGrow: 1, color: theme.palette.text.primary }}
              >
                Weightliftr
              </Typography>
            </Box>
            <List>
              <ListItem>
                <Typography>Quick Nav</Typography>
              </ListItem>

              {workoutTemplates.map((template) => {
                return (
                  <ListItemButton
                    onClick={() =>
                      handleNavigateToTemplateConfig(template.workout_id)
                    }
                  >
                    <ListItemIcon>
                      <PlayCircleFilledWhiteIcon />
                    </ListItemIcon>
                    <ListItemText primary={template.workout_text} />
                  </ListItemButton>
                );
              })}
              <Divider />
              <ListItemButton onClick={handleCreateBlankWorkout}>
                <ListItemIcon>
                  <AddCircleOutlineIcon />
                </ListItemIcon>
                <ListItemText primary="Create Blank Workout" />
              </ListItemButton>
              <ListItemButton onClick={handleViewTemplates}>
                <ListItemIcon>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary="View Templates" />
              </ListItemButton>
              <Divider />
              <ListItemButton onClick={handleNavigateHome}>
                <ListItemIcon>
                  <HomeIcon />
                </ListItemIcon>
                <Typography>Home</Typography>
              </ListItemButton>
              <Divider />
            </List>

            <MenuList>
              <MenuItem onClick={handleNavigateToWorkouts}>
                Past Workouts
              </MenuItem>
              <MenuItem onClick={handleToggleFeedbackDialog}>
                Give feedback!
              </MenuItem>
              <MenuItem onClick={() => navigate("/blog")}>Blog</MenuItem>
              {isInstallable ? (
                <MenuItem onClick={handleInstallClick}>Install App</MenuItem>
              ) : null}
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
              {admin ? (
                <MenuItem onClick={() => navigate("/admin")}>Admin</MenuItem>
              ) : null}
            </MenuList>
          </Drawer>

          <FeedbackDialog
            open={feedbackOpen}
            handleReportFeedback={handleReportFeedback}
            onClose={handleToggleFeedbackDialog}
          />
        </Box>
      )}
    </Box>
  );
};

export default NavBar;
